import type {
  LTLHazmatDetails,
  LTLQuoteLoadItemBreakdown,
} from '@store/ltl/types';
import type { HazmatDetails, LoadItemBreakdown } from '@store/orders/types';

// Sub-item weight or pieces moving more than this fraction away from the
// quoted value is flagged in the stops editor.
export const BREAKDOWN_DEVIATION_THRESHOLD = 0.1;

type BreakdownList = LoadItemBreakdown[] | null | undefined;

type QuotedBreakdown = {
  weight?: number | string | null;
  pieces?: number | string | null;
};

export type BreakdownDeviation = {
  field: 'weight' | 'pieces';
  subIndex: number;
  original: number;
  current: number;
};

export type BreakdownRollup = {
  weight: number | null;
  pieces: string | null;
  class: string | null;
  nmfc: string | null;
};

const isBlank = (value: unknown) =>
  value === null || value === undefined || value === '';

const toNumberOrNull = (value: unknown): number | null => {
  if (isBlank(value)) return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const toClassValue = (value: unknown) =>
  parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));

export const getActiveBreakdowns = (breakdowns: BreakdownList) =>
  (breakdowns ?? []).filter(
    (breakdown) => breakdown.operationType !== 'Delete',
  );

export const hasActiveBreakdowns = (breakdowns: BreakdownList) =>
  getActiveBreakdowns(breakdowns).length > 0;

export const sumBreakdownField = (
  breakdowns: BreakdownList,
  field: 'weight' | 'pieces',
): number | null => {
  const values = getActiveBreakdowns(breakdowns).map((breakdown) =>
    toNumberOrNull(breakdown[field]),
  );
  if (values.every((value) => value === null)) return null;
  return values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
};

export const getHighestClassBreakdown = (breakdowns: BreakdownList) =>
  getActiveBreakdowns(breakdowns).reduce<LoadItemBreakdown | undefined>(
    (highest, breakdown) => {
      const value = toClassValue(breakdown.class);
      if (Number.isNaN(value)) return highest;
      return highest === undefined || value > toClassValue(highest.class)
        ? breakdown
        : highest;
    },
    undefined,
  );

// The parent load item carries the summed weight and pieces, plus the class
// and NMFC of its highest-class sub-item — the same rule as LTL quoting.
// When that sub-item has no NMFC, the parent keeps its current one.
export const rollUpBreakdowns = (
  breakdowns: BreakdownList,
  currentNmfc: string | null = null,
): BreakdownRollup | null => {
  if (!hasActiveBreakdowns(breakdowns)) return null;
  const highestClass = getHighestClassBreakdown(breakdowns);
  const pieces = sumBreakdownField(breakdowns, 'pieces');
  return {
    weight: sumBreakdownField(breakdowns, 'weight'),
    pieces: pieces === null ? null : String(pieces),
    class: highestClass ? String(highestClass.class) : null,
    nmfc: isBlank(highestClass?.nmfc)
      ? currentNmfc
      : (highestClass?.nmfc ?? null),
  };
};

const getBreakdownErrors = (item: unknown) =>
  (item as { loadItemBreakdowns?: unknown } | undefined)?.loadItemBreakdowns;

// Yup errors for a later row leave empty slots before it, and felte drops
// those slots when merging, so the errors shift onto an earlier row. When any
// sub-item has errors, filling the empty load item and sub-item slots with
// empty objects keeps them on their own rows. Without sub-item errors (always
// the case with mixed commodity off) the errors are returned untouched.
export const fillBreakdownErrorHoles = <T>(errors: T): T => {
  const loadItems = (errors as { loadItems?: unknown } | undefined)?.loadItems;
  if (
    !Array.isArray(loadItems) ||
    !(loadItems as unknown[]).some((item) =>
      Array.isArray(getBreakdownErrors(item)),
    )
  ) {
    return errors;
  }
  return {
    ...errors,
    loadItems: Array.from(loadItems as unknown[], (item) => {
      const breakdowns = getBreakdownErrors(item);
      if (!Array.isArray(breakdowns)) return item ?? {};
      return {
        ...(item as object),
        loadItemBreakdowns: Array.from(
          breakdowns as unknown[],
          (breakdown) => breakdown ?? {},
        ),
      };
    }),
  };
};

export const createLoadItemBreakdown = (
  seed: Partial<LoadItemBreakdown> = {},
): LoadItemBreakdown => ({
  id: 0,
  operationType: 'Insert',
  description: '',
  class: null,
  nmfc: null,
  weight: null,
  pieces: null,
  hazmat: false,
  loadItemHazmatDetail: null,
  ...seed,
});

export const updateBreakdown = (
  breakdowns: LoadItemBreakdown[],
  subIndex: number,
  patch: Partial<LoadItemBreakdown>,
): LoadItemBreakdown[] =>
  breakdowns.map((breakdown, index) => {
    if (index !== subIndex) return breakdown;
    const next = { ...breakdown, ...patch };
    if (next.operationType === 'Insert' || next.operationType === 'Delete') {
      return next;
    }
    return { ...next, operationType: next.id > 0 ? 'Update' : 'Insert' };
  });

// Saved sub-items are soft-deleted so the API removes them; unsaved ones are dropped.
export const removeBreakdown = (
  breakdowns: LoadItemBreakdown[],
  subIndex: number,
): LoadItemBreakdown[] => {
  const target = breakdowns.at(subIndex);
  if (target === undefined) return breakdowns;
  if (target.id > 0) {
    return breakdowns.map((breakdown, index) =>
      index === subIndex
        ? { ...breakdown, operationType: 'Delete' }
        : breakdown,
    );
  }
  return breakdowns.filter((_, index) => index !== subIndex);
};

// A persisted hazmat detail is soft-deleted (not removed) when hazmat is
// turned off, so only a detail that is still active belongs on the clone;
// otherwise the copy would insert a stale detail on a non-hazmat row.
const hasActiveHazmatDetail = (breakdown: LoadItemBreakdown) =>
  breakdown.hazmat &&
  breakdown.loadItemHazmatDetail != null &&
  breakdown.loadItemHazmatDetail.operationType !== 'Delete';

export const cloneBreakdownsForInsert = (
  breakdowns: BreakdownList,
): LoadItemBreakdown[] =>
  getActiveBreakdowns(breakdowns).map((breakdown) => ({
    ...breakdown,
    id: 0,
    operationType: 'Insert',
    loadItemHazmatDetail: hasActiveHazmatDetail(breakdown)
      ? {
          ...(breakdown.loadItemHazmatDetail as HazmatDetails),
          operationType: 'Insert',
          loadItemId: 0,
        }
      : null,
  }));

export const mapLoadBreakdownsToQuoteBreakdowns = (
  breakdowns: BreakdownList,
): LTLQuoteLoadItemBreakdown[] =>
  getActiveBreakdowns(breakdowns).map((breakdown) => ({
    description: breakdown.description,
    class: isBlank(breakdown.class) ? null : String(breakdown.class),
    nmfc: breakdown.nmfc ?? null,
    weight: toNumberOrNull(breakdown.weight),
    pieces: isBlank(breakdown.pieces) ? null : String(breakdown.pieces),
    hazmat: Boolean(breakdown.hazmat),
    // A soft-deleted detail on a non-hazmat row must not reach the quote,
    // where any non-null child detail becomes quoteHazmatDetail.
    loadItemHazmatDetail: hasActiveHazmatDetail(breakdown)
      ? { ...(breakdown.loadItemHazmatDetail as HazmatDetails) }
      : null,
  }));

const toLoadHazmatDetail = (detail: LTLHazmatDetails): HazmatDetails => ({
  operationType: 'Insert',
  unNumber: detail.unNumber ?? '',
  packingGroup: detail.packingGroup ?? '',
  hazardClass: detail.hazardClass ?? '',
  piece: detail.piece ?? '',
  contactNumber: detail.contactNumber ?? '',
  contactName: detail.contactName ?? '',
  loadItemId: 0,
});

export const mapQuoteBreakdownsToLoadBreakdowns = (
  breakdowns: LTLQuoteLoadItemBreakdown[] | null | undefined,
): LoadItemBreakdown[] =>
  (breakdowns ?? []).map((breakdown) => {
    const hazmatDetail =
      breakdown.loadItemHazmatDetail ?? breakdown.quoteHazmatDetail;
    return createLoadItemBreakdown({
      description: breakdown.description,
      class: isBlank(breakdown.class) ? null : String(breakdown.class),
      nmfc: breakdown.nmfc ?? null,
      weight: toNumberOrNull(breakdown.weight),
      pieces: toNumberOrNull(breakdown.pieces),
      hazmat: Boolean(breakdown.hazmat),
      loadItemHazmatDetail:
        Boolean(breakdown.hazmat) && hazmatDetail
          ? toLoadHazmatDetail(hazmatDetail)
          : null,
    });
  });

const exceedsThreshold = (
  current: number,
  original: number,
  threshold: number,
) =>
  original === 0
    ? current !== 0
    : Math.abs(current - original) / Math.abs(original) > threshold;

// Sub-items are matched to the quote by position among active rows.
export const getBreakdownDeviations = (
  current: BreakdownList,
  quoted: QuotedBreakdown[] | null | undefined,
  threshold = BREAKDOWN_DEVIATION_THRESHOLD,
): BreakdownDeviation[] =>
  getActiveBreakdowns(current).flatMap((breakdown, subIndex) => {
    const quotedBreakdown = (quoted ?? []).at(subIndex);
    if (quotedBreakdown === undefined) return [];
    return (['weight', 'pieces'] as const).flatMap((field) => {
      const currentValue = toNumberOrNull(breakdown[field]);
      const originalValue = toNumberOrNull(quotedBreakdown[field]);
      if (
        currentValue === null ||
        originalValue === null ||
        !exceedsThreshold(currentValue, originalValue, threshold)
      ) {
        return [];
      }
      return [
        { field, subIndex, original: originalValue, current: currentValue },
      ];
    });
  });

export const formatBreakdownSummary = (breakdown: LoadItemBreakdown) => {
  const parts = [
    `${breakdown.pieces ?? 0} pcs`,
    `${breakdown.weight ?? 0} lbs`,
  ];
  if (Boolean(breakdown.nmfc)) parts.push(`NMFC ${breakdown.nmfc}`);
  if (Boolean(breakdown.class)) parts.push(`Class ${breakdown.class}`);
  return `↳ ${parts.join(' · ')} (${breakdown.description})`;
};
